<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { NIcon } from 'naive-ui';
import { IconChatActive } from '@/components/design/icons';
import GaiaChat from '@/components/design/domain/chat/GaiaChat.vue';
import GaiaWorkspacePane from '@/components/design/review/GaiaWorkspacePane.vue';
import { useAuthStore } from '@/stores/auth';
import { useReviewProjectStore } from '@/stores/reviewProjectStore';

/**
 * The chat rail of the review workspace (the mock's "Ask GAIA"): the same
 * GaiaChat the chat page and the home screen mount, initialised with the
 * `review_project` kind. Everything that makes it a project chat — which agent
 * answers, which threads its history lists, that a message carries the data
 * room's selection, that an attachment lands in the data room — follows from
 * that kind inside the component. This pane supplies the project it is about,
 * keeps the data-room selection in step, and owns the :deep height rule an
 * embedded chat needs (views carry no CSS of their own).
 *
 * It wears GaiaWorkspacePane like the data room, analysis and report do, so the
 * fourth surface of the workspace lines up with the other three by
 * construction: same header inset, same control sizes, same expand/close pair.
 */
const props = defineProps<{
  projectUid: string;
  uploadDirectoryUid: string | undefined;
  /** The data room's name, the upload dialog's first crumb. */
  uploadDirectoryLabel?: string;
  /** Widened to cover the pane grid — the workspace owns the flag. */
  expanded: boolean;
}>();

const emit = defineEmits<{
  close: [];
  'update:expanded': [expanded: boolean];
  'interrupt-decision': [decision: { interruptType: string | null; action: string }];
}>();

const router = useRouter();
const authStore = useAuthStore();
const reviewProjectStore = useReviewProjectStore();

const askValue = ref('');

/**
 * The data-room selection, named for the chat's context chips. Names come from
 * the cache the files list fills as its pages load, so a revision selected on a
 * page the user has seen is labelled; anything else falls back in the chip.
 */
const selectedRevisions = computed(() =>
  reviewProjectStore.dataRoomSelectedRevisionUids.map(uid => ({
    uid,
    name: reviewProjectStore.dataRoomFileNameByRevisionUid[uid],
  }))
);

/** Initials on the user's own chat bubbles — "Janina Möllmann" → "JM". */
const userInitials = computed(() =>
  (authStore.backendUser?.full_name ?? '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part.charAt(0).toUpperCase())
    .join('')
);

// Files uploaded through the composer become data-room files, so they join the
// selection the next message focuses on — like any file picked in the room.
function handleAddedRevisions(revisionUids: string[]): void {
  const merged = new Set([...reviewProjectStore.dataRoomSelectedRevisionUids, ...revisionUids]);
  reviewProjectStore.dataRoomSelectedRevisionUids = [...merged];
  // The files list can't learn about the new file on its own (its websocket only
  // covers rows it already shows) — poke it to refetch.
  reviewProjectStore.dataRoomFilesRefreshTick += 1;
}

function handleRemovedRevisions(revisionUids: string[]): void {
  const removed = new Set(revisionUids);
  reviewProjectStore.dataRoomSelectedRevisionUids =
    reviewProjectStore.dataRoomSelectedRevisionUids.filter(uid => !removed.has(uid));
}
</script>

<template>
  <gaia-workspace-pane
    title="Chat"
    divider="left"
    header-divider
    :scroll-body="false"
    expandable
    closable
    :expanded="props.expanded"
    expand-label="Widen the chat"
    contract-label="Narrow the chat"
    close-label="Close the chat"
    data-tour="rp.chat"
    @update:expanded="value => emit('update:expanded', value)"
    @close="emit('close')"
  >
    <template #icon>
      <n-icon :component="IconChatActive" :size="15" />
    </template>

    <div class="chat-panel__body">
      <!-- `chatting` is pinned, as on the chat page: a pane has no hero state to
           collapse back to, so the chat always shows its thread view. -->
      <gaia-chat
        v-model:value="askValue"
        chatting
        attachable
        chat-kind="review_project"
        :dense="!props.expanded"
        :review-project-uid="props.projectUid"
        :upload-directory-uid="props.uploadDirectoryUid"
        :upload-directory-label="props.uploadDirectoryLabel"
        :selected-revisions="selectedRevisions"
        :user-initials="userInitials"
        follow-up-placeholder="Ask a follow-up about this project…"
        placeholder="Ask about the files, analyses and findings in this project…"
        drop-hint="Drop files to add them to the data room"
        @clear-selected-revisions="reviewProjectStore.dataRoomSelectedRevisionUids = []"
        @add-selected-revisions="handleAddedRevisions"
        @remove-selected-revisions="handleRemovedRevisions"
        @interrupt-decision="emit('interrupt-decision', $event)"
        @open-reference="path => router.push(path)"
      />
    </div>
  </gaia-workspace-pane>
</template>

<style scoped>
/* Horizontally the pane's own inset, so the thread title lines up with "Chat"
   above it, its controls with the pane's, and the rule under it sits on one
   left edge with the header's.

   Vertically, the top matches the 14px the chat's own thread header pads below
   itself. Without it the thread title sat hard under the pane's rule with only
   its leading for company, while the rule below it kept its 14px — so the block
   read as pushed up against the header rather than set between the two rules.

   Not `pad-body`: that pads the bottom too, and the composer is meant to sit on
   the pane's floor. */
.chat-panel__body {
  flex: 1;
  min-height: 0;
  display: flex;
  padding: 14px var(--gaia-pane-inset) 0;
}

/* GaiaChat's chat mode sizes itself against the viewport (`100vh - 152px`) for
   a page host. In a pane the height is the pane's, so it fills what the header
   leaves instead. */
.chat-panel__body :deep(.gaia-chat--chatting) {
  height: 100%;
  min-height: 0;
}
</style>
